/**
 * Svelte 5 action: observe a node's visibility via IntersectionObserver
 * and fire `onChange(isIntersecting)` whenever it crosses the threshold.
 *
 * Usage:
 *   <div use:observeVisibility={{ onChange: (v) => visible = v }}></div>
 *
 * Options mirror IntersectionObserver init. We default to a generous
 * `rootMargin` so the chart starts animating *before* the user scrolls
 * it fully into view — better for editorial scroll experiences.
 */
export function observeVisibility(node, options) {
  let {
    onChange,
    rootMargin = '300px 0px 300px 0px',
    threshold = 0.15
  } = options || {};

  if (typeof IntersectionObserver === 'undefined') {
    // No IO support: be safe and treat the node as always visible so the
    // chart still works in legacy environments.
    onChange?.(true);
    return { destroy() {} };
  }

  const io = new IntersectionObserver(
    ([entry]) => onChange?.(!!entry.isIntersecting),
    { rootMargin, threshold }
  );
  io.observe(node);

  return {
    update(next) {
      onChange = next?.onChange ?? onChange;
    },
    destroy() {
      io.disconnect();
    }
  };
}
